/**
 * @type {firebase.FieldValue}
 */
let _FieldValue

/**
 * @type {firebase.firestore.Firestore}
 */
let _firestore

/**
 * @typedef ITraineeModel
 * @property {string} id
 * @property {boolean} paidEntranceFee
 * @property {boolean} paidLessonFee
 * @property {string} stripeCustomerId
 * @property {string} stripeEntranceFeeSubscriptionId
 * @property {string} stripeSubscriptionId
 * @property {'v2'} payment
 */

/**
 * @typedef ITrainerModel
 * @property {string} id
 */

/**
 * @typedef ISecretModel
 * @property {string} id
 */

/**
 * @typedef ICourseModel
 * @property {string} id
 */

/**
 * @typedef IReportModel
 * @property {string} id
 */

/**
 * @typedef IPrepaidPartnerModel
 * @property {string} id
 */

/**
 * @interface BaseCollection
 * @template TModel
 */
class BaseCollection {
  /**
   * @returns {firebase.firestore.CollectionReference}
   */
  get collection() {
    return _firestore.collection('__NONE__')
  }

  /**
   * @param {FirebaseFirestore.DocumentSnapshot<FirebaseFirestore.DocumentData>} doc
   * @returns {TModel}
   */
  getDataFromDocumentData(doc) {
    if (doc.exists) {
      return {
        ...doc.data(),
        id: doc.id
      }
    } else {
      return null
    }
  }

  /**
   * @returns {Promise<TModel[]>}
   */
  all() {
    return this.collection
      .get()
      .then(q => q.docs.map(this.getDataFromDocumentData))
  }

  /**
   * @returns {Promise<string[]>}
   */
  allIds() {
    return this.collection.get().then(q => q.docs.map(item => item.id))
  }

  /**
   * @param {string} id
   * @returns {Promise<TModel>}
   */
  findById(id) {
    return this.collection
      .doc(id)
      .get()
      .then(this.getDataFromDocumentData)
  }

  /**
   * @param {TModel} obj
   * @returns {Promise<void>}
   */
  add(obj) {
    return this.collection.add(obj).then(this.getDataFromDocumentData)
  }

  /**
   * @param {string} id
   * @param {TModel} obj
   * @returns {Promise<void>}
   */
  set(id, obj) {
    return this.collection.doc(id).set(obj)
  }

  /**
   * @param {string} id
   * @param {TModel} obj
   * @returns {Promise<void>}
   */
  update(id, obj) {
    return this.collection.doc(id).update(obj)
  }

  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  delete(id) {
    return this.collection.doc(id).delete()
  }

  /**
   * This callback is displayed as a global member.
   * @callback onUpdatedCallback
   * @param {TModel} obj
   */

  /**
   * Return unsubscribe function
   * @param {string} id
   * @param {onUpdatedCallback} callback
   * @returns {() => void}
   */
  onUpdated(id, callback) {
    return this.collection.doc(id).onSnapshot(next => {
      let obj = this.getDataFromDocumentData(next)
      callback(obj)
    })
  }
}

/**
 * @extends {BaseCollection<ITraineeModel>}
 */
class TraineeCollection extends BaseCollection {
  /**
   * @returns {firebase.firestore.CollectionReference}
   */
  get collection() {
    return _firestore.collection('USERS')
  }

  /**
   * @param {FirebaseFirestore.DocumentSnapshot<FirebaseFirestore.DocumentData>} doc
   * @returns {ITrainerModel}
   */
  getDataFromDocumentData(doc) {
    if (doc.exists) {
      return {
        ...doc.data(),
        uid: doc.id,
        id: doc.id
      }
    } else {
      return null
    }
  }
}

/**
 * @extends {BaseCollection<ITrainerModel>}
 */
class TrainerCollection extends BaseCollection {
  /**
   * @returns {firebase.firestore.CollectionReference}
   */
  get collection() {
    return _firestore.collection('TRAINERS')
  }

  /**
   * @param {string} id
   * @returns {Promise<TModel>}
   */
  findByUid(uid) {
    return this.collection
      .where("authedUid", "==", uid)
      .get()
      .then((q) => {
        if (q.docs.length > 0) {
          return this.getDataFromDocumentData(q.docs[0])
        } else {
          return null
        }
      })
  }
}

/**
 * @extends {BaseCollection<ISecretModel>}
 * @property {string} _traineeId
 */
class TraineeSecretCollection extends BaseCollection {
  /**
   * @param {string} traineeId
   */
  constructor(traineeId) {
    super()
    this._traineeId = traineeId
  }

  /**
   * @returns {firebase.firestore.CollectionReference}
   */
  get collection() {
    return _firestore
      .collection('USERS')
      .doc(this._traineeId)
      .collection('SECRETS')
  }
}

/**
 * @extends {BaseCollection<ITrainerSecretModel>}
 * @property {string} _trainerId
 */
class TrainerSecretCollection extends BaseCollection {
  /**
   * @param {string} trainerId
   */
  constructor(trainerId) {
    super()
    this._trainerId = trainerId
  }

  /**
   * @returns {firebase.firestore.CollectionReference}
   */
  get collection() {
    return _firestore
      .collection('TRAINERS')
      .doc(this._trainerId)
      .collection('SECRETS')
  }
}

/**
 * @extends {BaseCollection<ICourseModel>}
 */
class CourseCollection extends BaseCollection {
  /**
   * @returns {firebase.firestore.CollectionReference}
   */
  get collection() {
    return _firestore.collection('COURSES')
  }
}

/**
 * @extends {BaseCollection<IReportModel>}
 */
class ReportCollection extends BaseCollection {
  /**
   * @returns {firebase.firestore.CollectionReference}
   */
  get collection() {
    return _firestore.collection('REPORTS')
  }

  getReportByOptions({createdBy, course, lectureIndex}) {
    let query = this.collection
    if (createdBy) {
      query = query.where('createdBy', '==', createdBy)
    }
    if (course) {
      query = query.where('course', '==', course)
    }
    if (lectureIndex) {
      query = query.where('lectureIndex', '==', lectureIndex)
    }
    
    return query
      .get()
      .then((q) => {
        if (q.docs.length > 0) {
          return this.getDataFromDocumentData(q.docs[0])
        } else {
          return null
        }
      })
  }

  getReportsByOptions({createdBy, course, lectureIndex}) {
    let query = this.collection
    if (createdBy) {
      query = query.where('createdBy', '==', createdBy)
    }
    if (course) {
      query = query.where('course', '==', course)
    }
    if (lectureIndex) {
      query = query.where('lectureIndex', '==', lectureIndex)
    }
    
    return query
      .get()
      .then(q => q.docs.map(this.getDataFromDocumentData))
  }

  onListUpdated({ createdBy, course, lectureIndex }, callback) {
    let query = this.collection

    let shouldUseOrderBy = true
    if (createdBy) {
      query = query.where('createdBy', '==', createdBy)
      shouldUseOrderBy = false
    }
    if (course) {
      query = query.where('course', '==', course)
      shouldUseOrderBy = false
    }
    if (lectureIndex) {
      query = query.where('lectureIndex', '==', lectureIndex)
      shouldUseOrderBy = false
    }

    if (shouldUseOrderBy) {
      query = query.orderBy('updatedAt', 'desc')
    }
    
    return query
      .onSnapshot(next => {
        let objs = next.docs.map(this.getDataFromDocumentData)
        callback(objs)
      })
  }
}

/**
 * @extends {BaseCollection<IPaymentHistoryModel>}
 * @property {string} _userId
 */
 class PaymentHistoryCollection extends BaseCollection {
  /**
   * @param {string} userId
   */
  constructor(userId) {
    super()
    this._userId = userId
  }

  /**
   * @returns {firebase.firestore.CollectionReference}
   */
  get collection() {
    return _firestore
      .collection('USERS')
      .doc(this._userId)
      .collection('PAYMENT_HISTORY')
  }

  /**
   * @param {string} string
   * @returns {Promise<IPaymentHistoryModel>}
   */
   getLatestPaymentHistory() {
    return this.collection
      .orderBy('createdAt', 'desc')
      .get()
      .then((q) => {
        if (q.docs.length > 0) {
          return this.getDataFromDocumentData(q.docs[0])
        } else {
          return ''
        }
      })
  }
}

/**
 * @extends {BaseCollection<IPrepaidPartnerModel>}
 */
 class PrepaidPartnerCollection extends BaseCollection {
  /**
   * @returns {firebase.firestore.CollectionReference}
   */
  get collection() {
    return _firestore.collection('PREPAID_PARTNER')
  }

  /**
   * @param {string} string
   * @returns {Promise<IPrepaidPartnerModel>}
   */
   findByName(name) {
    return this.collection
      .where("name", "==", name)
      .get()
      .then((q) => {
        if (q.docs.length > 0) {
          return this.getDataFromDocumentData(q.docs[0])
        } else {
          return null
        }
      })
  }
}

/**
 * The whole database
 */
const database = {
  getTrainee(traineeId) {
    return new TraineeCollection().findById(traineeId)
  },

  setTrainee(traineeId, obj) {
    return new TraineeCollection().set(traineeId, obj)
  },

  updateTrainee(traineeId, obj) {
    return new TraineeCollection().update(traineeId, obj)
  },

  /**
   * 
   * @param {string} traineeId 
   * @param {(obj: ITraineeModel) => void} callback 
   * @returns 
   */
  onTraineeUpdated(traineeId, callback) {
    return new TraineeCollection().onUpdated(traineeId, callback)
  },

  getTrainer(trainerId) {
    return new TrainerCollection().findById(trainerId)
  },

  getTrainerByUid(trainerUid) {
    return new TrainerCollection().findByUid(trainerUid)
  },

  setTrainer(trainerId, obj) {
    return new TrainerCollection().set(trainerId, obj)
  },

  updateTrainer(trainerId, obj) {
    return new TrainerCollection().update(trainerId, obj)
  },

  getTraineeEmail(traineeId) {
    return new TraineeSecretCollection(traineeId).findById('email')
  },

  setTraineeEmail(traineeId, emailObj) {
    return new TraineeSecretCollection(traineeId).set('email', emailObj)
  },

  getTrainerEmail(trainerId) {
    return new TrainerSecretCollection(trainerId).findById('email')
  },

  setTrainerEmail(trainerId, emailObj) {
    return new TrainerSecretCollection(trainerId).set('email', emailObj)
  },

  addPlan(traineeId, planId) {
    return new TraineeCollection().update(traineeId, {
      plans: _FieldValue.arrayUnion(planId)
    })
  },

  getCourse(courseId) {
    return new CourseCollection().findById(courseId)
  },

  getReport(reportId) {
    return new ReportCollection().findById(reportId)
  },

  getReportByOptions({createdBy, course, lectureIndex}) {
    return new ReportCollection().getReportByOptions({
      createdBy,
      course,
      lectureIndex,
    })
  },

  getReportsByOptions({createdBy, course, lectureIndex}) {
    return new ReportCollection().getReportsByOptions({
      createdBy,
      course,
      lectureIndex,
    })
  },

  getAllCourses() {
    return new CourseCollection().all()
  },

  setReport(reportId, obj) {
    return new ReportCollection().set(reportId, obj)
  },

  updateReport(reportId, obj) {
    return new ReportCollection().update(reportId, obj)
  },

  onReportsUpdated({ createdBy, course, lectureIndex }, callback) {
    return new ReportCollection().onListUpdated({ createdBy, course, lectureIndex }, callback)
  },

  getPaymentHistories(userId) {
    return new PaymentHistoryCollection(userId).all()
  },

  setPaymentHistory(userId, paymentHistoryId, obj) {
    return new PaymentHistoryCollection(userId).set(paymentHistoryId, obj)
  },

  getApplication(traineeId) {
    return new TraineeSecretCollection(traineeId).findById('application')
  },

  getPrepaidPartnerByName(name) {
    return new PrepaidPartnerCollection().findByName(name)
  },

  getLatestPaymentHistory(userId) {
    return new PaymentHistoryCollection(userId).getLatestPaymentHistory()
  }
}

/**
 * @param {firebase.firestore.Firestore} firestore
 * @param {Object} param1
 * @param {firebase.firestore.FieldValue} param1.FieldValue
 */
const initializeDatabase = (firestore, { FieldValue }) => {
  console.log('initializeDatabase')
  _FieldValue = FieldValue
  _firestore = firestore
}

module.exports = {
  database,
  initializeDatabase
}
